import { formatLocalDateTime } from '../../utils/localDateTime';
import React, { useState, useEffect } from 'react';
import type { EpOpsAnalysisResult, Page, NavParams, SrmProject } from '@types';
import { ICONS, RISK_LEVEL_COLORS } from '../constants';
import { createSafeMarkdown } from '../../utils/safeMarkdown';
import { 
    Section, 
    DefList, 
    Indicator 
} from './ReportShared';
import useGoogleMaps from '../useGoogleMaps';
import UnifiedMap from '../UnifiedMap';

const md = createSafeMarkdown();

interface EpOpsAnalysisDisplayProps {
    result: EpOpsAnalysisResult;
    onSaveUpdate?: (updatedData: any) => Promise<void>;
    isSaving?: boolean;
    onNavigate?: (nav: { page: Page; params?: NavParams }) => void;
    srmProject?: SrmProject;
    srmModule?: any;
}

const EditableField: React.FC<{
    label: string;
    value: string;
    onSave: (val: string) => void;
    isMultiline?: boolean;
    isEditing: boolean;
    setIsEditing: (val: boolean) => void;
}> = ({ label, value, onSave, isMultiline, isEditing, setIsEditing }) => {
    const [tempValue, setTempValue] = useState(value);

    if (isEditing) {
        return (
            <div className="space-y-2 animate-in fade-in slide-in-from-top-1 duration-200">
                <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest">{label}</label>
                {isMultiline ? (
                    <textarea 
                        className="w-full p-3 bg-slate-900 text-white border border-indigo-500 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none min-h-[120px]"
                        value={tempValue}
                        onChange={(e) => setTempValue(e.target.value)}
                    />
                ) : (
                    <input 
                        className="w-full p-3 bg-slate-900 text-white border border-indigo-500 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                        value={tempValue}
                        onChange={(e) => setTempValue(e.target.value)}
                    />
                )}
                <div className="flex gap-2">
                    <button 
                        onClick={() => { onSave(tempValue); setIsEditing(false); }}
                        className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-[10px] font-bold uppercase rounded transition-colors"
                    >
                        Apply Change
                    </button>
                    <button 
                        onClick={() => { setTempValue(value); setIsEditing(false); }}
                        className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-white text-[10px] font-bold uppercase rounded transition-colors"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div 
            onClick={() => setIsEditing(true)}
            className="group relative cursor-pointer hover:bg-slate-50 p-2 -m-2 rounded-lg transition-all"
        >
            <div className="prose prose-sm max-w-none text-slate-700" dangerouslySetInnerHTML={{ __html: md.render(value || '') }} />
            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity text-indigo-500">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
            </div>
        </div>
    );
};

const EpOpsAnalysisDisplay: React.FC<EpOpsAnalysisDisplayProps> = ({ 
    result: initialResult, 
    onSaveUpdate, 
    isSaving,
    onNavigate,
    srmProject,
    srmModule
}) => {
    const [data, setData] = useState<EpOpsAnalysisResult>(initialResult);
    const [editingField, setEditingField] = useState<string | null>(null);
    const { isLoaded } = useGoogleMaps();
    const [mapFocus, setMapFocus] = useState<{ lat: number; lng: number; zoom?: number } | null>(null);

    useEffect(() => {
        // 1. Prioritize Project/Operation Location
        if (srmProject?.lat && srmProject?.lng && Number(srmProject.lat) !== 0) {
            setMapFocus({ lat: Number(srmProject.lat), lng: Number(srmProject.lng), zoom: 14 });
            return;
        } 
        
        // 2. Fallback to first valid Marker
        if (data.mapMarkers && data.mapMarkers.length > 0) {
            const firstValidMarker = data.mapMarkers.find(m => m.lat !== 0 && m.lng !== 0);
            if (firstValidMarker) {
                setMapFocus({ lat: firstValidMarker.lat, lng: firstValidMarker.lng, zoom: 14 });
                return;
            }
        } 
        
        // 3. Last Resort: Geocode the Country/Location
        if (data.country && data.country !== 'Unknown Country' && isLoaded && typeof google !== 'undefined') {
            const geocoder = new google.maps.Geocoder();
            geocoder.geocode({ address: data.country }, (results, status) => {
                if (status === 'OK' && results?.[0]?.geometry?.location) {
                    const loc = results[0].geometry.location;
                    setMapFocus({ lat: loc.lat(), lng: loc.lng(), zoom: 6 });
                }
            });
        }
    }, [srmProject, data.mapMarkers, data.country, isLoaded]);

    const updateField = (path: string, value: any) => {
        const newData = { ...data };
        const keys = path.split('.');
        let current: any = newData;
        for (let i = 0; i < keys.length - 1; i++) {
            current = current[keys[i]];
        }
        current[keys[keys.length - 1]] = value;
        setData(newData);
    };

    const handleSaveReassessment = async () => {
        if (onSaveUpdate) {
            await onSaveUpdate(data);
        }
    };

    const handleAlertClick = (alert: any) => {
        if (alert.lat && alert.lng) {
            setMapFocus({ lat: alert.lat, lng: alert.lng, zoom: 16 });
        }
    };

    const pmesii = data.pmesiiAnalysis || {
        political: 'N/A', military: 'N/A', economic: 'N/A', social: 'N/A', infrastructure: 'N/A', information: 'N/A'
    };

    const carver = data.carverAssessment || {
        criticality: 0, accessibility: 0, recuperability: 0, vulnerability: 0, effect: 0, recognizability: 0,
        overallAssessment: 'Not assessed using high-fidelity model.'
    };

    // Sub-component for Tactical Alert Sidebar
    const TacticalAlertSidebar = () => (
        <div className="bg-slate-900 rounded-2xl border border-slate-800 flex flex-col h-full sticky top-4 max-h-[calc(100vh-2rem)]">
            <div className="p-4 border-b border-slate-800 flex items-center justify-between">
                <h3 className="text-[10px] font-black uppercase tracking-widest text-indigo-400">Tactical Event Feed</h3>
                <span className="flex h-2 w-2 rounded-full bg-green-500 animate-pulse"></span>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar">
                {(!data.alerts || data.alerts.length === 0) ? (
                    <p className="text-[10px] text-slate-500 italic text-center py-8">No active tactical alerts in immediate AO.</p>
                ) : (
                    (data.alerts || []).map((alert) => (
                        <div 
                            key={alert.id} 
                            onClick={() => handleAlertClick(alert)}
                            className="group p-3 bg-slate-800/50 rounded-xl border border-slate-700 hover:border-indigo-500/50 cursor-pointer transition-all active:scale-[0.98]"
                        >
                            <div className="flex justify-between items-start mb-2">
                                <span className={`px-1.5 py-0.5 rounded text-[8px] font-black uppercase tracking-tighter ${
                                    alert.severity === 'Critical' ? 'bg-red-500 text-white' :
                                    alert.severity === 'High' ? 'bg-orange-500 text-white' :
                                    alert.severity === 'Medium' ? 'bg-yellow-500 text-slate-900' :
                                    'bg-blue-500 text-white'
                                }`}>
                                    {alert.severity}
                                </span>
                                <span className="text-[8px] text-slate-500 font-mono">{formatLocalDateTime(alert.timestamp, alert.timestamp || '')}</span>
                            </div>
                            <h4 className="text-[11px] font-bold text-white group-hover:text-indigo-300 transition-colors">{alert.summary}</h4>
                            <p className="text-[10px] text-slate-400 line-clamp-2 mt-1 leading-tight">{alert.description}</p>
                            <div className="mt-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                <span className="text-[8px] font-black text-indigo-500 uppercase tracking-widest">Focus Map</span>
                            </div>
                        </div>
                    ))
                )}
            </div>
            <div className="p-3 bg-slate-800/80 border-t border-slate-800">
                <div className="flex items-center gap-2">
                    <div className="h-1 w-1 rounded-full bg-indigo-500"></div>
                    <span className="text-[8px] text-slate-400 font-bold uppercase tracking-widest">Strategic Sync: ACTIVE</span>
                </div>
            </div>
        </div>
    );

    return (
        <div className="space-y-6 animate-in fade-in duration-500 pb-12">
            {/* Tactical Header */}
            <div className="bg-slate-900 p-6 rounded-2xl border border-slate-800 text-white shadow-2xl relative overflow-hidden">
                <div className="absolute top-0 right-0 p-4 opacity-10">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-32 w-32" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                </div>
                <div className="relative z-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                    <div>
                        <div className="flex items-center gap-3 mb-2">
                            <span className="px-2 py-0.5 bg-indigo-500 text-[10px] font-black uppercase tracking-widest rounded">Operational Intelligence</span>
                            <span className="px-2 py-0.5 bg-slate-700 text-[10px] font-black uppercase tracking-widest rounded">SOF/USSS Framework</span>
                        </div>
                        <h1 className="text-3xl font-black uppercase tracking-tighter">
                            EP Operations <span className="text-indigo-400">{'&'}</span> Risk Analysis
                        </h1>
                        <p className="text-slate-400 text-sm font-bold uppercase tracking-widest mt-1">
                            Mission AOR: {data.country}
                        </p>
                    </div>
                    {onSaveUpdate && (
                        <button 
                            onClick={handleSaveReassessment}
                            disabled={isSaving}
                            className="bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-700 text-white px-8 py-4 rounded-xl font-black uppercase tracking-widest text-xs transition-all shadow-xl shadow-indigo-500/20 active:scale-95 flex items-center gap-3"
                        >
                            {isSaving ? (
                                <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                            )}
                            Save Mission Reassessment
                        </button>
                    )}
                </div>
            </div>

            {/* Tactical Awareness Map Section */}
            <div className="bg-slate-900 rounded-2xl border border-slate-800 p-2 overflow-hidden shadow-2xl relative">
                <div className="h-[400px] w-full rounded-xl overflow-hidden relative grayscale-[0.5] hover:grayscale-0 transition-all duration-700">
                    {isLoaded ? (
                        <UnifiedMap 
                            isLoaded={isLoaded}
                            focusedLocation={mapFocus}
                            markers={data.mapMarkers || []}
                            heatmapPoints={data.threatHeatmap || []}
                            hideSidebar={true}
                            mapTitle="Mission Tactical Overlay"
                        />
                    ) : (
                        <div className="absolute inset-0 flex items-center justify-center bg-slate-900">
                            <div className="text-center">
                                <div className="animate-spin h-10 w-10 border-4 border-indigo-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                                <p className="text-slate-500 text-[10px] font-black uppercase tracking-widest">Initializing Tactical Optic...</p>
                            </div>
                        </div>
                    )}
                </div>
                <div className="absolute top-4 left-4 z-10 flex flex-col gap-2 pointer-events-none">
                    <div className="bg-slate-900/90 backdrop-blur-md p-3 rounded-xl border border-slate-700 shadow-xl pointer-events-auto max-w-[200px]">
                        <h3 className="text-[10px] font-black uppercase tracking-widest text-indigo-400 mb-2">Tactical Grounding</h3>
                        <div className="flex flex-col gap-2">
                            <div className="flex items-center gap-2">
                                <div className="h-2 w-2 rounded-full bg-indigo-500"></div>
                                <span className="text-[8px] text-slate-300 font-bold uppercase">Operational HQ</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="h-2 w-2 rounded-full bg-red-500"></div>
                                <span className="text-[8px] text-slate-300 font-bold uppercase">Medical / Trauma</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="h-2 w-2 rounded-full bg-blue-600"></div>
                                <span className="text-[8px] text-slate-300 font-bold uppercase">Airports / Ports</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="h-2 w-2 rounded-full bg-violet-600"></div>
                                <span className="text-[8px] text-slate-300 font-bold uppercase">Border Crossings</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="h-2 w-2 rounded-full bg-navy-900" style={{backgroundColor: '#1E3A8A'}}></div>
                                <span className="text-[8px] text-slate-300 font-bold uppercase">Police Stations</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="h-2 w-2 rounded-full bg-orange-600"></div>
                                <span className="text-[8px] text-slate-300 font-bold uppercase">High Threat Zones</span>
                            </div>
                            {data.threatHeatmap && data.threatHeatmap.length > 0 && (
                                <div className="mt-1 flex items-center gap-2">
                                    <div className="h-2 w-full bg-gradient-to-r from-transparent via-yellow-500 to-red-600 rounded"></div>
                                    <span className="text-[8px] text-slate-400 font-black uppercase whitespace-nowrap">Heat Map</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Dashboard Workspace */}
            <div className="flex flex-col lg:flex-row gap-6 items-start">
                {/* Main Intelligence Body */}
                <div className="flex-1 space-y-6 min-w-0 order-2 lg:order-1">

            {/* Tactical Summary Section */}
            <Section title="Tactical Operational Summary">
                <EditableField 
                    label="Operational Overview"
                    value={data.tacticalSummary}
                    onSave={(val) => updateField('tacticalSummary', val)}
                    isMultiline
                    isEditing={editingField === 'tacticalSummary'}
                    setIsEditing={(val) => setEditingField(val ? 'tacticalSummary' : null)}
                />
            </Section>

            {/* PMESII-PT Intelligence Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(pmesii).map(([key, value]) => (
                    <div key={key} className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm hover:border-indigo-300 transition-colors">
                        <span className="text-[10px] font-black text-indigo-500 uppercase tracking-widest block mb-2">{key} Analysis</span>
                        <div className="text-xs text-slate-700 leading-relaxed max-h-32 overflow-y-auto">
                            {value as string}
                        </div>
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* CARVER Assessment */}
                <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200">
                    <h3 className="text-sm font-black uppercase tracking-widest text-slate-500 mb-6 flex items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                        CARVER Vulnerability Profile
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
                        {['criticality', 'accessibility', 'recuperability', 'vulnerability', 'effect', 'recognizability'].map((factor) => (
                            <div key={factor} className="bg-white p-3 rounded-lg border border-slate-200 text-center">
                                <span className="text-[9px] font-black uppercase text-slate-400 tracking-wider block truncate">{factor}</span>
                                <span className="text-xl font-black text-slate-900">{(carver as any)[factor]}</span>
                            </div>
                        ))}
                    </div>
                    <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-inner">
                        <span className="text-[10px] font-black text-indigo-500 uppercase tracking-widest block mb-2">Vulnerability Assessment</span>
                        <p className="text-xs text-slate-600 leading-relaxed italic">
                            {carver.overallAssessment}
                        </p>
                    </div>
                </div>

                {/* Scenarios (MLCOA/MDCOA) */}
                <div className="space-y-4">
                    <div className="p-6 bg-blue-50/50 rounded-2xl border border-blue-100">
                        <h4 className="text-[10px] font-black uppercase tracking-widest text-blue-600 mb-3">Targeted Scenarios (MLCOA)</h4>
                        <h5 className="text-sm font-black text-slate-900 mb-1">{data.scenarios?.mlcoa?.title || 'Standard Operational Baseline'}</h5>
                        <p className="text-xs text-slate-600 leading-relaxed">{data.scenarios?.mlcoa?.description || 'Routine operational environment expected.'}</p>
                    </div>
                    <div className="p-6 bg-red-50/50 rounded-2xl border border-red-100">
                        <h4 className="text-[10px] font-black uppercase tracking-widest text-red-600 mb-3">Hardened Threats (MDCOA)</h4>
                        <h5 className="text-sm font-black text-slate-900 mb-1">{data.scenarios?.mdcoa?.title || 'Worst-Case Breach'}</h5>
                        <p className="text-xs text-slate-600 leading-relaxed">{data.scenarios?.mdcoa?.description || 'Contingency plans for critical security failure.'}</p>
                    </div>
                </div>
            </div>

            {/* Tactical Logistics, C2 & HQ */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Section title="Tactical Logistics">
                    <DefList items={[
                        { term: 'Communications', definition: data.tacticalLogistics?.communications || 'Standard COMMS' },
                        { term: 'Administration', definition: data.tacticalLogistics?.administration || 'Standard ADMIN' },
                        { term: 'Transport', definition: data.tacticalLogistics?.transport || 'Unarmored Standard' },
                        { term: 'Equipment', definition: data.tacticalLogistics?.equipment || 'Full Kit' },
                    ]} />
                </Section>

                <Section title="Command & Control (C2)">
                    <div className="space-y-4">
                        <DefList items={[
                            { term: 'Structure', definition: data.commandAndControl?.structure || 'Standard OIC/AIC Hierarchy' },
                            { term: 'Comms Protocol', definition: data.commandAndControl?.communicationsProtocol || 'Encrypted / P2P / Satellite' },
                            { term: 'Escalation', definition: data.commandAndControl?.escalationPath || 'Standard S.O.P.' },
                        ]} />
                        {data.commandAndControl?.liaisonProtocols && (
                            <div className="mt-2 bg-indigo-50/50 p-3 rounded border border-indigo-100 text-[11px] text-indigo-700 italic">
                                <span className="font-black uppercase tracking-tighter block mb-1">Liaison Profile</span>
                                {data.commandAndControl.liaisonProtocols}
                            </div>
                        )}
                    </div>
                </Section>

                <Section title="Operational HQ (OPCEN)">
                    <div className="space-y-4">
                        <DefList items={[
                            { term: 'Setup', definition: data.operationalHQ?.setup || 'Fixed Tactical Base' },
                            { term: 'Location Req', definition: data.operationalHQ?.locationRequirements || 'Offset / Hardened' },
                            { term: 'Equipment', definition: data.operationalHQ?.equipment || 'Full Signal/TOC Suite' },
                        ]} />
                        {data.operationalHQ?.redundancyPlan && (
                            <div className="mt-2 bg-slate-100 p-3 rounded border border-slate-200 text-[11px] text-slate-600 italic">
                                <span className="font-black uppercase tracking-tighter block mb-1">Redundancy Plan</span>
                                {data.operationalHQ.redundancyPlan}
                            </div>
                        )}
                    </div>
                </Section>
            </div>

            {/* Team Composition Builder */}
            <Section title="Operational Team Compilation">
                <div className="bg-slate-900 rounded-2xl overflow-hidden shadow-2xl">
                    <table className="w-full text-left text-xs">
                        <thead>
                            <tr className="bg-slate-800 text-slate-400 font-black uppercase tracking-widest">
                                <th className="p-4">Role / Title</th>
                                <th className="p-4">QTY</th>
                                <th className="p-4">Tactical Requirements / Skillset</th>
                            </tr>
                        </thead>
                        <tbody className="text-slate-300">
                                {data.teamComposition?.roles?.map((role, idx) => (
                                    <tr key={idx} className="border-t border-slate-800 hover:bg-slate-800/50 transition-colors">
                                        <td className="p-4 font-bold text-white">{role.role}</td>
                                        <td className="p-4">{role.count}</td>
                                        <td className="p-4 text-slate-400 italic">{role.requirements}</td>
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                    <div className="p-4 bg-indigo-900/20 border-t border-slate-800 mt-auto">
                        <span className="text-[10px] font-black text-indigo-400 uppercase tracking-widest block mb-1">Compilation Summary</span>
                        <p className="text-xs text-slate-300 italic">{data.teamComposition?.suggestedCompilation}</p>
                    </div>
                </div>
            </Section>

            {/* Personnel Qualifications Matrix */}
            {data.personnelQualifications && data.personnelQualifications.length > 0 && (
                <Section title="Personnel Qualification Matrix">
                    <div className="bg-white rounded-2xl overflow-hidden border border-slate-200 shadow-xl overflow-x-auto">
                        <table className="w-full text-left text-xs min-w-[800px]">
                            <thead>
                                <tr className="bg-slate-50 text-slate-500 font-black uppercase tracking-widest border-b border-slate-200">
                                    <th className="p-4">Role</th>
                                    <th className="p-4">Education</th>
                                    <th className="p-4">Skills</th>
                                    <th className="p-4">Knowledge</th>
                                    <th className="p-4">Experience</th>
                                    <th className="p-4">Training</th>
                                </tr>
                            </thead>
                            <tbody className="text-slate-700">
                                {(data.personnelQualifications || []).map((p, idx) => (
                                    <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
                                        <td className="p-4 font-black text-indigo-600">{p.role}</td>
                                        <td className="p-4 text-[10px] leading-tight">{p.education}</td>
                                        <td className="p-4 text-[10px] leading-tight">{p.skills}</td>
                                        <td className="p-4 text-[10px] leading-tight">{p.knowledge}</td>
                                        <td className="p-4 text-[10px] leading-tight">{p.experience}</td>
                                        <td className="p-4">
                                            <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded text-[9px] font-bold">
                                                {p.training}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Section>
            )}

            {/* Threats Grid */}
            <Section title="Intelligence-Led Mitigation Framework">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {(data.threatsToPersonnel || []).map((item, idx) => (
                        <div key={idx} className="p-4 bg-white rounded-xl border border-slate-200 shadow-sm border-l-4 border-l-orange-500">
                            <h4 className="text-sm font-black text-slate-900 mb-2 flex items-center gap-2">
                                <span className="h-2 w-2 rounded-full bg-orange-500 animate-pulse"></span>
                                {item.threat}
                            </h4>
                            <div className="bg-slate-50 p-3 rounded-lg border border-slate-100 italic text-[11px] text-slate-600">
                                <span className="text-[9px] font-black text-slate-400 uppercase tracking-tighter block mb-1">Mitigation Directive</span>
                                {item.mitigation}
                            </div>
                        </div>
                    ))}
                </div>
            </Section>

            {/* Medical & Law Enforcement */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Section title="Trauma Care & TCCC">
                    <DefList items={[
                        { term: 'Quality of Care', definition: data.medicalAssessment?.qualityOfCare || 'Standard Local Care' },
                        { term: 'Host Nation Facilities', definition: data.medicalAssessment?.recommendedFacilities || 'Level 1 Trauma Centers' },
                        { term: 'Case Management', definition: data.medicalAssessment?.emergencyContacts || 'Global Response Centers' },
                    ]} />
                </Section>
                <Section title="Host Nation Liaison">
                    <DefList items={[
                        { term: 'Jurisdiction', definition: data.lawEnforcement?.disposition || 'Local Civil/Military Jurisdiction' },
                        { term: 'Integrity Rating', definition: data.lawEnforcement?.corruptionLevel || 'Moderate / Unrated' },
                        { term: 'Coordination', definition: data.lawEnforcement?.contactInfo || 'Local Emergency Services' },
                    ]} />
                </Section>
            </div>
            
            {data.additionalSections && data.additionalSections.length > 0 && (
                <div className="space-y-6 mt-6">
                    {data.additionalSections.map((section: any, index: number) => (
                        <Section key={index} title={section.title || section.heading || 'Operational Directive'}>
                            <div className="prose prose-slate max-w-none text-slate-700" dangerouslySetInnerHTML={{ __html: md.render(section.content || '') }} />
                        </Section>
                    ))}
                </div>
            )}
        </div>

                {/* Tactical Alert Side Column */}
                <div className="w-full lg:w-[320px] order-1 lg:order-2">
                    <TacticalAlertSidebar />
                </div>
            </div>

            <div className="bg-slate-900 p-8 rounded-3xl border border-slate-800 text-center relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 via-transparent to-transparent opacity-50"></div>
                <div className="relative z-10">
                    <p className="text-slate-400 text-[10px] font-black uppercase tracking-[0.2em] mb-4">Operational Status</p>
                    <h2 className="text-2xl font-black text-white uppercase tracking-tighter mb-4 italic">Analysis Finalized</h2>
                    <p className="text-slate-500 text-xs max-w-lg mx-auto leading-relaxed mb-8">
                        {data.disclaimer}
                    </p>
                    <div className="inline-flex items-center gap-3 px-4 py-2 bg-indigo-500/10 rounded-full border border-indigo-500/30">
                        <span className="h-2 w-2 rounded-full bg-indigo-500 animate-pulse"></span>
                        <span className="text-indigo-400 text-[10px] font-black uppercase tracking-widest">Active Intelligence Session</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EpOpsAnalysisDisplay;
